<template>
    <!-- "On this page" is the part that is only true here: under an answer
         the pills are about what the answer named; in an empty box they are
         about the page the box opened over, and the label is what says so.
         This is also the only thing the box says before being asked anything
         — deliberately a statement about your business rather than a
         greeting. -->
    <AskNoticed
        :suggestions="suggestions"
        label="Gotoko noticed on this page"
        data-testid="ask-page-noticed"
        @open="(identity) => emit('open', identity)"
    />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import AskNoticed from "@/components/AskBox/AskNoticed.vue";
import type { Suggestion } from "@/lib/openapi/gen/types.gen";
import { useAskStore } from "@/stores/ask-store";
import { getEntityInfo, routeCarriesEntityId } from "@/stores/entity-store";

/**
 * What gotoko has noticed about the page the box opened over.
 *
 * Rendered only while the conversation is empty: the box has nothing to say
 * yet, so it says what the landing page would say about the entity you are
 * looking at — at most two lines, in the page's words. Once a question is
 * asked this leaves and does not return; a conversation with history is not
 * an empty box.
 *
 * Three places it says nothing, each on purpose:
 *
 * - **The landing page.** That page already speaks first, and two surfaces
 *   saying the same thing about the same stek is the confusion this whole
 *   arrangement exists to avoid. The split is: the page speaks about the
 *   business, the box about where you are and what you asked.
 * - **A page with no entity** — an organization page, the stek's own pages.
 *   `getEntityInfo` falls back to the stek itself there, and no finding is
 *   about a stek.
 * - **A page whose entity no finding can be about** — the server answers with
 *   an empty list, and an empty list renders nothing.
 *
 * Its own component for the reason `AskAnswer` and `AskReferences` are: it
 * reaches the router, and `AskBox.spec.ts` mounts without one.
 */
const emit = defineEmits<{ open: [identity: string] }>();

const route = useRoute();
const store = useAskStore();

const suggestions = ref<Suggestion[]>([]);

const pageEntity = computed(() => {
    if (route.name === "personalHome") {
        return undefined;
    }
    if (!routeCarriesEntityId(route.params)) {
        return undefined;
    }
    const info = getEntityInfo(route.params);
    if (!info?.entityId || !info.entityType) {
        return undefined;
    }
    if (info.entityType === "stek" || info.entityType === "organization") {
        return undefined;
    }
    return { entity_type: `${info.entityType}`, entity_id: info.entityId };
});

// Latest page wins: a fast navigation while a fetch is out must not land the
// previous page's cards under a box that is now over a different one.
let requested = 0;
watch(
    () =>
        pageEntity.value &&
        `${pageEntity.value.entity_type}:${pageEntity.value.entity_id}`,
    async () => {
        const entity = pageEntity.value;
        const token = ++requested;
        if (!entity) {
            suggestions.value = [];
            return;
        }
        const found = await store.noticedAbout(entity);
        if (token === requested) {
            suggestions.value = found;
        }
    },
    { immediate: true }
);
</script>
